import type { StateTree, Store, StoreGeneric } from 'pinia';
import type { AnyObjectSchema } from 'yup';
import { useGlobalStore } from '@/features/Global/store';
import type { Sparte } from '@/features/Global/types';
import {
  usePfeContractStore,
  contractSchema as pfeContractStore,
} from '@/features/Sparten/pfe/useContractStore';
import {
  inputSchema as PfeSchema,
  usePfeInputStore,
} from '@/features/Sparten/pfe/useInputStore';
import {
  usePkwContractStore,
  contractSchema as pkwContractSchema,
} from '@/features/Sparten/pkw/useContractStore';
import {
  inputSchema as PkwSchema,
  usePkwInputStore,
} from '@/features/Sparten/pkw/useInputStore';
import {
  usePkwv2ContractStore,
  contractSchema as pkwv2ContractSchema,
} from '@/features/Sparten/pkwv2/useContractStore';
import {
  inputSchema as Pkwv2Schema,
  usePkwv2InputStore,
} from '@/features/Sparten/pkwv2/useInputStore';
import ResConfig from '@/features/Sparten/res';
import {
  useResContractStore,
  contractSchema as resContractSchema,
} from '@/features/Sparten/res/useContractStore';
import {
  inputSchema as ResSchema,
  useResInputStore,
} from '@/features/Sparten/res/useInputStore';
import {
  inputSchema as NutzSchema,
  useNutzInputStore,
} from '@/features/Sparten/nutz/useInputStore';
import {
  inputSchema as CampSchema,
  useCampInputStore,
} from '@/features/Sparten/camp/useInputStore';
import {
  inputSchema as Campv2Schema,
  useCampv2InputStore,
} from '@/features/Sparten/campv2/useInputStore';
import {
  inputSchema as KradSchema,
  useKradInputStore,
} from '@/features/Sparten/krad/useInputStore';
import {
  inputSchema as Kradv2Schema,
  useKradv2InputStore,
} from '@/features/Sparten/kradv2/useInputStore';
import {
  inputSchema as Nutzv2Schema,
  useNutzv2InputStore,
} from '@/features/Sparten/nutzv2/useInputStore';
import type {
  ContractSchemaRegistry,
  ContractStoreRegistry,
  InputSchemaRegistry,
  InputStoreRegistry,
  SpartenConfigModules,
  WebreportsConfiguration,
} from '@/features/Sparten/types';
import { WEBREPORT_TYPE } from '@/features/Sparten/types';
import { config as PkwConfig } from '@/features/Sparten/pkw';
import { config as Pkwv2Config } from '@/features/Sparten/pkwv2';
import { config as HunConfig } from '@/features/Sparten/hun';
import { config as PfeConfig } from '@/features/Sparten/pfe';
import { config as PhvConfig } from '@/features/Sparten/phv';
import { config as NutzConfig } from '@/features/Sparten/nutz';
import { config as HauConfig } from '@/features/Sparten/hau';
import { config as CampConfig } from '@/features/Sparten/camp';
import { config as Campv2Config } from '@/features/Sparten/campv2';
import { config as KradConfig } from '@/features/Sparten/krad';
import { config as Kradv2Config } from '@/features/Sparten/kradv2';
import { config as Nutzv2Config } from '@/features/Sparten/nutzv2';
import { config as WohConfig } from '@/features/Sparten/woh';
import { config as UnfConfig } from '@/features/Sparten/unf';
import { config as PkvConfig } from '@/features/Sparten/pkv';
import { config as BuConfig } from '@/features/Sparten/bu';
import { config as RlvConfig } from '@/features/Sparten/rlv';
import {
  inputSchema as PhvSchema,
  usePhvInputStore,
} from '@/features/Sparten/phv/useInputStore';
import {
  inputSchema as BuSchema,
  useBuInputStore,
} from '@/features/Sparten/bu/useInputStore';
import {
  usePhvContractStore,
  contractSchema as phvContractSchema,
} from '@/features/Sparten/phv/useContractStore';
import {
  useNutzContractStore,
  contractSchema as nutzContractSchema,
} from '@/features/Sparten/nutz/useContractStore';
import {
  inputSchema as HauSchema,
  useHauInputStore,
} from '@/features/Sparten/hau/useInputStore';
import { useHauContractStore } from '@/features/Sparten/hau/useContractStore';
import { contractSchema as hauContractSchema } from '@/features/Sparten/hau/schema/contractSchema';
import {
  useHunInputStore,
  inputSchema as HunSchema,
} from '@/features/Sparten/hun/useInputStore';
import {
  useHunContractStore,
  contractSchema as hunContractSchema,
} from '@/features/Sparten/hun/useContractStore';
import {
  useCampContractStore,
  contractSchema as campContractSchema,
} from '@/features/Sparten/camp/useContractStore';
import {
  useCampv2ContractStore,
  contractSchema as campv2ContractSchema,
} from '@/features/Sparten/campv2/useContractStore';
import {
  useKradContractStore,
  contractSchema as kradContractSchema,
} from '@/features/Sparten/krad/useContractStore';
import {
  useKradv2ContractStore,
  contractSchema as kradv2ContractSchema,
} from '@/features/Sparten/kradv2/useContractStore';
import {
  useNutzv2ContractStore,
  contractSchema as nutzv2ContractSchema,
} from '@/features/Sparten/nutzv2/useContractStore';
import {
  inputSchema as WohSchema,
  useWohInputStore,
} from '@/features/Sparten/woh/useInputStore';
import { contractSchema as wohContractSchema } from '@/features/Sparten/woh/schema/contractSchema';
import { useWohContractStore } from '@/features/Sparten/woh/useContractStore';
import {
  usePkvInputStore,
  inputSchema as PkvSchema,
} from '@/features/Sparten/pkv/useInputStore';
import {
  inputSchema as UnfSchema,
  useUnfInputStore,
} from '@/features/Sparten/unf/useInputStore';
import {
  useUnfContractStore,
  contractSchema as unfContractSchema,
} from '@/features/Sparten/unf/useContractStore';
import {
  usePkvContractStore,
  contractSchema as pkvContractSchema,
} from '@/features/Sparten/pkv/useContractStore';
import {
  useBuContractStore,
  contractSchema as buContractSchema,
} from '@/features/Sparten/bu/useContractStore';
import {
  useRlvInputStore,
  inputSchema as RlvSchema,
} from '@/features/Sparten/rlv/useInputStore';
import {
  useRlvContractStore,
  contractSchema as rlvContractSchema,
} from '@/features/Sparten/rlv/useContractStore';

let inputStoreRegistry: InputStoreRegistry | undefined;

// If you don't provide a specific sparte, the input store of the current global sparte will be returned
export const useInputStore = (sparte: Sparte | null = null): StoreGeneric => {
  const storeSparte = sparte ?? useGlobalStore().getSparte;

  if (inputStoreRegistry === undefined) {
    inputStoreRegistry = {
      pfe: () => usePfeInputStore(),
      hun: () => useHunInputStore(),
      pkw: () => usePkwInputStore(),
      pkwv2: () => usePkwv2InputStore(),
      res: () => useResInputStore(),
      phv: () => usePhvInputStore(),
      nutz: () => useNutzInputStore(),
      hau: () => useHauInputStore(),
      camp: () => useCampInputStore(),
      campv2: () => useCampv2InputStore(),
      krad: () => useKradInputStore(),
      kradv2: () => useKradv2InputStore(),
      nutzv2: () => useNutzv2InputStore(),
      woh: () => useWohInputStore(),
      pkv: () => usePkvInputStore(),
      unf: () => useUnfInputStore(),
      bu: () => useBuInputStore(),
      rlv: () => useRlvInputStore(),
    };
  }

  return inputStoreRegistry[storeSparte]();
};

let inputSchemaRegistry: InputSchemaRegistry | undefined;

export const useInputStoreSchema = (
  sparte: Sparte | null = null,
): AnyObjectSchema => {
  const storeSparte = sparte ?? useGlobalStore().getSparte;

  if (inputSchemaRegistry === undefined) {
    inputSchemaRegistry = {
      pfe: () => PfeSchema,
      hun: () => HunSchema,
      pkw: () => PkwSchema,
      pkwv2: () => Pkwv2Schema,
      res: () => ResSchema,
      phv: () => PhvSchema,
      nutz: () => NutzSchema,
      hau: () => HauSchema,
      camp: () => CampSchema,
      campv2: () => Campv2Schema,
      krad: () => KradSchema,
      kradv2: () => Kradv2Schema,
      nutzv2: () => Nutzv2Schema,
      woh: () => WohSchema,
      pkv: () => PkvSchema,
      unf: () => UnfSchema,
      bu: () => BuSchema,
      rlv: () => RlvSchema,
    };
  }

  return inputSchemaRegistry[storeSparte]();
};

let contractStoreRegistry: ContractStoreRegistry | undefined;

export const useContractStore = <T extends StateTree>(
  sparte: Sparte | null = null,
): Store<string, T> => {
  const storeSparte = sparte ?? useGlobalStore().getSparte;

  if (contractStoreRegistry === undefined) {
    contractStoreRegistry = {
      pfe: () => usePfeContractStore(),
      hun: () => useHunContractStore(),
      pkw: () => usePkwContractStore(),
      pkwv2: () => usePkwv2ContractStore(),
      res: () => useResContractStore(),
      phv: () => usePhvContractStore(),
      nutz: () => useNutzContractStore(),
      hau: () => useHauContractStore(),
      camp: () => useCampContractStore(),
      campv2: () => useCampv2ContractStore(),
      krad: () => useKradContractStore(),
      kradv2: () => useKradv2ContractStore(),
      nutzv2: () => useNutzv2ContractStore(),
      woh: () => useWohContractStore(),
      pkv: () => usePkvContractStore(),
      unf: () => useUnfContractStore(),
      bu: () => useBuContractStore(),
      rlv: () => useRlvContractStore(),
    };
  }

  return contractStoreRegistry[storeSparte]() as Store<string, T>;
};

let contractSchemaRegistry: ContractSchemaRegistry | undefined;

export const useContractStoreSchema = (
  sparte: Sparte | null = null,
): AnyObjectSchema => {
  const storeSparte = sparte ?? useGlobalStore().getSparte;

  if (contractSchemaRegistry === undefined) {
    contractSchemaRegistry = {
      pfe: () => pfeContractStore,
      hun: () => hunContractSchema,
      pkw: () => pkwContractSchema,
      pkwv2: () => pkwv2ContractSchema,
      res: () => resContractSchema,
      phv: () => phvContractSchema,
      nutz: () => nutzContractSchema,
      hau: () => hauContractSchema,
      camp: () => campContractSchema,
      campv2: () => campv2ContractSchema,
      krad: () => kradContractSchema,
      kradv2: () => kradv2ContractSchema,
      nutzv2: () => nutzv2ContractSchema,
      woh: () => wohContractSchema,
      pkv: () => pkvContractSchema,
      unf: () => unfContractSchema,
      bu: () => buContractSchema,
      rlv: () => rlvContractSchema,
    };
  }

  return contractSchemaRegistry[storeSparte]();
};

export const webreportsConfiguration: WebreportsConfiguration[] = [
  {
    type: WEBREPORT_TYPE.COMPARISON,
    urlPath: 'vergleich',
    component: () => import('@/views/Layouts/Webreports/Comparison.vue'),
  },
  {
    type: WEBREPORT_TYPE.OFFERS,
    urlPath: 'beitragsuebersicht',
    component: () => import('@/views/Layouts/Webreports/Tariffs.vue'),
  },
];

const spartenConfigModules: SpartenConfigModules = {
  pfe: PfeConfig,
  hun: HunConfig,
  pkw: PkwConfig,
  pkwv2: Pkwv2Config,
  res: ResConfig,
  phv: PhvConfig,
  nutz: NutzConfig,
  hau: HauConfig,
  camp: CampConfig,
  campv2: Campv2Config,
  krad: KradConfig,
  kradv2: Kradv2Config,
  nutzv2: Nutzv2Config,
  woh: WohConfig,
  pkv: PkvConfig,
  unf: UnfConfig,
  bu: BuConfig,
  rlv: RlvConfig,
};

export default spartenConfigModules;
